@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.TicketPolicy
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

/**
 * The customer-facing portal at /customer. Anyone not in supportUsers signs
 * in here with just their email and an emailed one-time pin (or by scanning a
 * "sign in on your phone" QR code from a signed-in device), and sees tickets
 * whose contactEmails includes that verified email - or, with company-wide
 * access set in Settings > Customers > Emails, every ticket of their company.
 */
internal object CustomerPortal {
    private const val EMAIL_KEY = "zi-customer-email"
    private const val COMPANY_KEY = "zi-customer-company"
    private const val LOGIN_AT_KEY = "zi-customer-login-at"
    /** This sign-in's portalSessions record (More > Portal usage on the support side). */
    private const val SESSION_KEY = "zi-customer-session"
    private var sessionActive = false
    private var watchingVisibility = false
    private const val SESSION_MILLIS = 30 * 24 * 60 * 60 * 1000.0
    private var expiryTimer: Int? = null
    private var otpChallenge = ""
    /** A QR sign-in is being redeemed: keep the loading splash up instead of flashing the email step. */
    private var redeeming = false
    private var otpDestination = ""
    private var pendingEmail = ""
    private var customerEmail = ""
    private var companyId = ""
    private var access = "own"
    private var stageFilter: String? = null
    private var search = ""
    private var tickets: List<WebRecord> = emptyList()
    /** Newest tickets loaded; "Load older tickets" adds a page. Opening the portal reads only these, not the whole history. */
    private const val TICKET_PAGE = 50
    private var ticketLimit = TICKET_PAGE
    private var ticketsStop: dynamic = null
    private var commentsStop: dynamic = null
    private var openTicketPath = ""

    fun start() {
        applyTheme(window.localStorage.getItem("support-theme") ?: "system")
        redeemHandoff()
        CustomerFirebaseGateway.observeAuth { user ->
            if (user == null && redeeming) return@observeAuth
            revealApp()
            if (user == null) {
                customerEmail = ""; companyId = ""
                if (otpChallenge.isBlank()) showEmailStep() else showOtpStep()
            } else if (sessionRemaining() <= 0) {
                // A customer session lasts 30 days from verifying the code; after that a new code is needed.
                logout()
            } else {
                expiryTimer?.let { window.clearTimeout(it) }
                expiryTimer = window.setTimeout({ logout() }, sessionRemaining().toInt())
                val savedEmail = window.localStorage.getItem(EMAIL_KEY)
                val savedCompany = window.localStorage.getItem(COMPANY_KEY)
                if (!savedEmail.isNullOrBlank() && !savedCompany.isNullOrBlank()) {
                    customerEmail = savedEmail; companyId = savedCompany
                    showPortal()
                } else {
                    promise(CustomerFirebaseGateway.idTokenClaims(user), { result ->
                        val claimEmail = field(result.claims, "customerEmail")
                        val claimCompany = field(result.claims, "companyId")
                        if (claimEmail.isBlank() || claimCompany.isBlank()) {
                            CustomerFirebaseGateway.logout()
                        } else {
                            customerEmail = claimEmail; companyId = claimCompany
                            window.localStorage.setItem(EMAIL_KEY, claimEmail)
                            window.localStorage.setItem(COMPANY_KEY, claimCompany)
                            showPortal()
                        }
                    }, { CustomerFirebaseGateway.logout() })
                }
            }
        }
    }

    private fun showEmailStep() {
        element("app")?.innerHTML = """
          <main class="login-page">
            <section class="login-brand">
              <div class="login-mark"><img class="brand-logo login-logo" src="/zora_innovations_logo.png" alt="Zora Innovations"><span>Customer Portal</span></div><h1>Your support tickets,<br>in one place.</h1>
              <p>Sign in with your email to view tickets raised for you and send us updates.</p>
              <div class="trust"><span><i class="material-symbols-rounded">mail</i> Email sign-in</span><span><i class="material-symbols-rounded">visibility</i> Only your tickets</span></div>
            </section>
            <section class="login-panel"><form id="customer-email-form" class="login-card" novalidate>
              <div class="login-kicker">Zora Innovations</div><h2>View your tickets</h2><p>Enter the email address your support ticket was raised with.</p>
              <div class="field"><label for="customer-email">Email address</label><input class="input" id="customer-email" type="email" autocomplete="email" required></div>
              <button id="customer-email-button" class="btn btn-primary btn-block" type="submit"><span class="material-symbols-rounded">mail</span> Send me a code</button>
              <p id="customer-email-error" style="color:var(--danger);margin-top:14px"></p>
            </form></section>
          </main>
        """.trimIndent()
        document.getElementById("customer-email-form")?.addEventListener("submit", { event ->
            event.preventDefault()
            val email = input("customer-email")
            if (email.isBlank()) return@addEventListener
            (element("customer-email-button") as? HTMLButtonElement)?.disabled = true
            element("customer-email-button")?.textContent = "Sending…"
            promise(CustomerFirebaseGateway.callable("requestCustomerLoginOtp", json("email" to email)), { result ->
                pendingEmail = email
                otpChallenge = field(result.data, "challengeId")
                otpDestination = field(result.data, "maskedEmail")
                showOtpStep()
            }, { message ->
                (element("customer-email-button") as? HTMLButtonElement)?.disabled = false
                element("customer-email-button")?.innerHTML = "<span class=\"material-symbols-rounded\">mail</span> Send me a code"
                element("customer-email-error")?.textContent = message
            })
        })
    }

    private fun showOtpStep() {
        element("app")?.innerHTML = """
          <main class="login-page">
            <section class="login-brand"><div class="login-mark"><img class="brand-logo login-logo" src="/zora_innovations_logo.png" alt="Zora Innovations"><span>Customer Portal</span></div><h1>Check your email.</h1><p>We verify every sign-in to keep your support tickets private.</p><div class="trust"><span><i class="material-symbols-rounded">shield_lock</i> One-time pin</span></div></section>
            <section class="login-panel"><form id="customer-otp-form" class="login-card" novalidate>
              <div class="login-kicker">Identity verification</div><h2>Enter your code</h2><p>We sent a four-digit code to <strong>${escape(otpDestination)}</strong>. It expires in 10 minutes.</p>
              <div class="field"><label for="customer-otp">Verification code</label><input class="input otp-code" id="customer-otp" inputmode="numeric" autocomplete="one-time-code" maxlength="4" pattern="[0-9]{4}" placeholder="0000" required></div>
              <button id="customer-otp-button" class="btn btn-primary btn-block" type="submit"><span class="material-symbols-rounded">verified_user</span> Verify and continue</button>
              <button id="customer-otp-back" class="btn btn-ghost btn-block" type="button">Use a different email</button>
              <p id="customer-otp-error" style="color:var(--danger);margin-top:14px"></p>
            </form></section>
          </main>
        """.trimIndent()
        document.getElementById("customer-otp-form")?.addEventListener("submit", { event -> event.preventDefault(); verifyOtp() })
        type("customer-otp") {
            val node = document.getElementById("customer-otp") as? org.w3c.dom.HTMLInputElement
            node?.value = node?.value?.filter { it.isDigit() }?.take(4).orEmpty()
        }
        click("customer-otp-back") { otpChallenge = ""; otpDestination = ""; pendingEmail = ""; showEmailStep() }
    }

    private fun verifyOtp() {
        val code = input("customer-otp")
        if (code.length != 4) { element("customer-otp-error")?.textContent = "Enter the four-digit code."; return }
        (element("customer-otp-button") as? HTMLButtonElement)?.disabled = true
        element("customer-otp-button")?.textContent = "Verifying…"
        promise(CustomerFirebaseGateway.callable("verifyCustomerLoginOtp", json("challengeId" to otpChallenge, "code" to code)), { result ->
            completeSignIn(result.data, { otpChallenge = ""; otpDestination = ""; pendingEmail = "" }) { message ->
                (element("customer-otp-button") as? HTMLButtonElement)?.disabled = false
                element("customer-otp-button")?.innerHTML = "<span class=\"material-symbols-rounded\">verified_user</span> Verify and continue"
                element("customer-otp-error")?.textContent = message
            }
        }, { message ->
            (element("customer-otp-button") as? HTMLButtonElement)?.disabled = false
            element("customer-otp-button")?.innerHTML = "<span class=\"material-symbols-rounded\">verified_user</span> Verify and continue"
            element("customer-otp-error")?.textContent = message
        })
    }

    /** Signs in with a token from verifyCustomerLoginOtp or redeemCustomerHandoff and starts the 30-day session. */
    private fun completeSignIn(data: dynamic, done: () -> Unit, failed: (String) -> Unit) {
        // Forget any previous customer first, so the auth listener can't show their tickets for this sign-in.
        window.localStorage.removeItem(EMAIL_KEY); window.localStorage.removeItem(COMPANY_KEY)
        window.localStorage.setItem(LOGIN_AT_KEY, kotlin.js.Date.now().toString())
        window.localStorage.setItem(SESSION_KEY, field(data, "sessionId"))
        promise(CustomerFirebaseGateway.loginWithCustomToken(field(data, "customToken")), {
            window.localStorage.setItem(EMAIL_KEY, field(data, "email"))
            window.localStorage.setItem(COMPANY_KEY, field(data, "companyId"))
            done()
        }, failed)
    }

    /** Opened from a "sign in on your phone" QR code (/customer#handoff=<id>): trade the one-use code for a sign-in. */
    private fun redeemHandoff() {
        val id = window.location.hash.removePrefix("#handoff=").takeIf { it != window.location.hash && it.isNotBlank() } ?: return
        window.history.replaceState(null, "", window.location.pathname)  // the code is single-use; don't leave it in the address bar
        redeeming = true
        // On failure, fall back to the email step with the reason.
        fun failed(message: String) { redeeming = false; revealApp(); showEmailStep(); toast(message, true) }
        promise(CustomerFirebaseGateway.callable("redeemCustomerHandoff", json("id" to id)), { result ->
            completeSignIn(result.data, { redeeming = false }, ::failed)
        }, ::failed)
    }

    /** Shows a QR code the phone camera opens to sign in to this same account; it works once, for 2 minutes. */
    private fun showHandoff() {
        promise(CustomerFirebaseGateway.callable("createCustomerHandoff"), { result ->
            val url = "${window.location.origin}/customer#handoff=${field(result.data, "id")}"
            promise(js("require('qrcode')").toDataURL(url, json("width" to 320, "margin" to 1)), { image ->
                modal("Sign in on your phone", """
                  <div style="text-align:center"><img src="$image" alt="Sign-in QR code" width="240" height="240" style="border-radius:12px;background:#fff;padding:8px"></div>
                  <p>Point your phone's camera at this code and open the link. You'll be signed in as <strong>${escape(customerEmail)}</strong> without needing a code.</p>
                  <p class="field-help">It works once and expires in 2 minutes. Only show it to yourself.</p>
                """.trimIndent())
            })
        })
    }

    private fun isDarkTheme(): Boolean = document.documentElement?.getAttribute("data-theme") == "dark"

    private fun toggleTheme() {
        val mode = if (isDarkTheme()) "light" else "dark"
        applyTheme(mode)
        window.localStorage.setItem("support-theme", mode)
        element("customer-theme-toggle")?.innerHTML = "<span class=\"material-symbols-rounded\">${if (isDarkTheme()) "light_mode" else "dark_mode"}</span>"
    }

    private fun greeting(): String {
        val hour = js("new Date().getHours()") as Int
        return if (hour < 12) "Good morning" else if (hour < 17) "Good afternoon" else "Good evening"
    }

    private fun showPortal() {
        element("app")?.innerHTML = """
          <main class="main customer-shell">
            <header class="topbar customer-topbar"><div class="cp-brand"><img class="brand-logo" src="/zora_innovations_logo.png" alt="" style="width:40px;height:40px;padding:6px"><strong>Zora Innovations Support</strong></div>
              <div class="top-actions"><button id="customer-new-ticket" class="btn btn-primary"><span class="material-symbols-rounded">add</span> New ticket</button><button id="customer-handoff" class="btn btn-icon" title="Sign in on your phone" aria-label="Sign in on your phone"><span class="material-symbols-rounded">qr_code_2</span></button><button id="customer-theme-toggle" class="btn btn-icon" title="Toggle dark mode" aria-label="Toggle dark mode"><span class="material-symbols-rounded">${if (isDarkTheme()) "light_mode" else "dark_mode"}</span></button><button id="customer-logout" class="btn btn-icon" title="Log out" aria-label="Log out"><span class="material-symbols-rounded">logout</span></button></div>
            </header>
            <section id="content" class="content cp-page">
              <header class="cp-hello">
                <p>${greeting()}</p>
                <h1>Where your requests are</h1>
                <p class="cp-who">Signed in as <strong>${escape(customerEmail)}</strong></p>
              </header>
              <nav id="customer-route" class="cp-route" aria-label="Show tickets at one stage"></nav>
              <div class="cp-list-head">
                <h2 id="customer-tickets-heading">Your tickets</h2>
                <div class="search-shell cp-search"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="customer-search">Search tickets</label><input id="customer-search" class="input search" type="search" placeholder="Search by number or title"></div>
              </div>
              <div id="customer-ticket-grid" class="cp-list"><div class="cp-empty">Loading your tickets…</div></div>
            </section>
          </main>
        """.trimIndent()
        click("customer-new-ticket") { openNewTicket() }
        click("customer-theme-toggle") { toggleTheme() }
        click("customer-logout") {
            confirmModal("Log out?", "You'll need a new code from your email to sign in again.", "customer-logout-confirm", "Log out", icon = "logout")
            click("customer-logout-confirm") {
                closeModal()
                // Stamp the logout before signing out; sign out regardless if that write fails.
                val stamp = touchSession("logoutAt" to CustomerFirebaseGateway.serverTimestamp())
                if (stamp == null) logout() else stamp.then({ logout() }, { logout() })
            }
        }
        startHeartbeat()
        click("customer-handoff") { showHandoff() }
        stageFilter = null; search = ""; ticketLimit = TICKET_PAGE
        element("customer-search")?.addEventListener("input", { search = input("customer-search").lowercase(); drawTickets() })
        // The access level lives on the customer's own record; company-wide access lists every company ticket.
        promise(CustomerFirebaseGateway.getDocument("customerEmails/$companyId/addresses/${customerEmail.replace("/", "%2F")}"), { snapshot ->
            access = if (snapshot.exists() == true) field(snapshot.data(), "access").ifBlank { "own" } else "own"
            listenTickets()
        }, { access = "own"; listenTickets() })
    }

    private fun listenTickets() {
        val fs = CustomerFirebaseGateway.firestore
        val companyWide = access == "company"
        element("customer-tickets-heading")?.textContent = if (companyWide) "All ${companyId.replaceFirstChar { it.uppercase() }} tickets" else "Your tickets"
        val query = if (companyWide) fs.query(CustomerFirebaseGateway.collection("companies/$companyId/tickets"), fs.orderBy("createdAt", "desc"), fs.limit(ticketLimit))
            else fs.query(
                CustomerFirebaseGateway.collection("companies/$companyId/tickets"),
                fs.where("contactEmails", "array-contains", customerEmail),
                fs.orderBy("createdAt", "desc"),
                fs.limit(ticketLimit)
            )
        cancelListener(ticketsStop)
        ticketsStop = CustomerFirebaseGateway.listenQuery(query, { rows -> tickets = rows; drawTickets() }, { message ->
            element("customer-ticket-grid")?.innerHTML = "<div class=\"empty\">${escape(message)}</div>"
        })
    }

    /** The stages a ticket travels through, in order, like a consignment on its route. */
    /** Assigned and started are one stage here: support often completes an assigned ticket without starting it first. */
    private val STAGES = listOf("open" to "Received", "in_progress" to "Assigned & in progress", "completed" to "Completed")

    /** The route across the top: how many tickets sit at each stop. Tapping a stop shows only those tickets. */
    private fun drawRoute() {
        element("customer-route")?.innerHTML = STAGES.joinToString("") { (stage, label) ->
            val count = tickets.count { ticketState(it.data) == stage }
            val chosen = stageFilter == stage
            """<button type="button" class="cp-stop cp-stop-$stage${if (count > 0) " has-tickets" else ""}" data-stage="$stage" aria-pressed="$chosen"><span class="cp-dot" aria-hidden="true"></span><strong>$count</strong><span>$label</span></button>"""
        }
        val nodes = document.querySelectorAll("[data-stage]")
        for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node ->
            node.addEventListener("click", { val stage = node.getAttribute("data-stage"); stageFilter = if (stageFilter == stage) null else stage; drawTickets() })
        }
    }

    /** Marks this session as seen now (plus any [extra] fields); null when there is no session record. */
    private fun touchSession(vararg extra: Pair<String, Any?>): dynamic {
        val id = window.localStorage.getItem(SESSION_KEY)?.takeIf { it.isNotBlank() } ?: return null
        val write = CustomerFirebaseGateway.update("portalSessions/$id", json("lastSeenAt" to CustomerFirebaseGateway.serverTimestamp(), *extra))
        write.catch { _: dynamic -> null }
        return write
    }

    private fun countActivity(counter: String) { touchSession(counter to CustomerFirebaseGateway.increment(1)) }

    /**
     * Stamps lastSeenAt on opening, on coming back to the tab and on leaving it (plus every action), not on a timer:
     * a few writes per visit instead of one every couple of minutes while a tab sits open.
     */
    private fun startHeartbeat() {
        sessionActive = true
        touchSession()
        if (watchingVisibility) return
        watchingVisibility = true
        document.addEventListener("visibilitychange", { if (sessionActive) touchSession() })
    }

    private fun logout() {
        sessionActive = false
        window.localStorage.removeItem(SESSION_KEY)
        cancelListener(ticketsStop); cancelListener(commentsStop)
        window.localStorage.removeItem(EMAIL_KEY)
        window.localStorage.removeItem(COMPANY_KEY)
        window.localStorage.removeItem(LOGIN_AT_KEY)
        expiryTimer?.let { window.clearTimeout(it) }; expiryTimer = null
        CustomerFirebaseGateway.logout()
    }

    /** Milliseconds left in the 30-day customer session; sessions without a login time count as expired. */
    private fun sessionRemaining(): Double {
        val loginAt = window.localStorage.getItem(LOGIN_AT_KEY)?.toDoubleOrNull() ?: return 0.0
        return loginAt + SESSION_MILLIS - kotlin.js.Date.now()
    }

    private fun ticketState(data: dynamic): String {
        val raw = TicketPolicy.canonicalStatus(field(data, "status"))
        val assigned = field(data, "assignedToUid").isNotBlank()
        return when {
            TicketPolicy.isCompleted(raw) -> "completed"
            raw == "in_progress" || assigned -> "in_progress"
            else -> "open"
        }
    }

    private fun stateLabel(s: String) = when (s) { "open" -> "Open"; "in_progress" -> "Assigned & in progress"; else -> s.replaceFirstChar { it.uppercase() } }

    private fun drawTickets() {
        drawRoute()
        val root = element("customer-ticket-grid") ?: return
        if (tickets.isEmpty()) {
            root.innerHTML = """
              <div class="cp-empty">
                <h3>No requests yet</h3>
                <p>When something isn't working, tell us here and you can follow it from received to completed.</p>
                <button id="customer-empty-new-ticket" class="btn btn-primary"><span class="material-symbols-rounded" aria-hidden="true">add</span> Raise a ticket</button>
              </div>
            """.trimIndent()
            click("customer-empty-new-ticket") { openNewTicket() }
            return
        }
        val shown = tickets.filter { row ->
            (stageFilter == null || ticketState(row.data) == stageFilter) &&
                (search.isBlank() || listOf(field(row.data, "ticketNumber"), field(row.data, "title"), field(row.data, "description")).any { it.lowercase().contains(search) })
        }
        if (shown.isEmpty()) {
            val stage = STAGES.firstOrNull { it.first == stageFilter }?.second?.lowercase()
            root.innerHTML = """<div class="cp-empty"><p>${if (search.isNotBlank()) "No tickets match “${escape(search)}”." else "No tickets are $stage right now."}</p><button id="customer-clear-filter" class="btn">Show all tickets</button></div>"""
            click("customer-clear-filter") { stageFilter = null; search = ""; (element("customer-search") as? HTMLInputElement)?.value = ""; drawTickets() }
            return
        }
        root.innerHTML = shown.joinToString("") { row ->
            val d = row.data
            val s = ticketState(d)
            val step = STAGES.indexOfFirst { it.first == s }
            val number = field(d, "ticketNumber").ifBlank { row.id }
            val title = field(d, "title").ifBlank { "Support request" }
            val assignee = field(d, "assignedToUsername")
            val who = when { s == "completed" -> "Completed"; assignee.isBlank() -> "Waiting for a technician"; else -> "${escape(assignee)} is on it" }
            val mini = STAGES.indices.joinToString("") { "<i${if (it <= step) " class=\"on\"" else ""}></i>" }
            """<button type="button" class="cp-row cp-row-$s" data-ticket="${escape(row.path)}" aria-label="$number: ${escape(title)}, ${STAGES[step].second}">
                <span class="cp-num">${escape(number)}</span>
                <span class="cp-title"><strong>${escape(title)}</strong><small>$who</small></span>
                <span class="cp-mini" aria-hidden="true">$mini<em>${STAGES[step].second}</em></span>
                <span class="cp-when">${escape(relativeTime(d.updatedAt ?: d.createdAt))}</span>
                <span class="material-symbols-rounded cp-chevron" aria-hidden="true">chevron_right</span>
            </button>"""
        } + if (tickets.size >= ticketLimit) "<button id=\"customer-load-more\" type=\"button\" class=\"btn cp-load-more\">Load older tickets</button>" else ""
        click("customer-load-more") { ticketLimit += TICKET_PAGE; listenTickets() }
        document.querySelectorAll("[data-ticket]").let { nodes ->
            for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { event ->
                val path = (event.currentTarget as HTMLElement).getAttribute("data-ticket") ?: return@addEventListener
                tickets.firstOrNull { it.path == path }?.let(::openDetail)
            })
        }
    }

    private fun openDetail(record: WebRecord) {
        openTicketPath = record.path
        countActivity("ticketsOpened")
        val d = record.data
        val s = ticketState(d)
        val number = field(d, "ticketNumber").ifBlank { record.id }
        val priority = field(d, "priority").ifBlank { "normal" }
        val assignee = field(d, "assignedToUsername").ifBlank { "Waiting to be assigned" }
        val step = STAGES.indexOfFirst { it.first == s }
        val title = field(d, "title").ifBlank { "Support request" }
        val description = field(d, "description")
        val resolutionHtml = if (s == "completed" && field(d, "resolution").isNotBlank())
            "<section class=\"cp-resolution\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">task_alt</span><div><label>How we fixed it</label><p>${escape(field(d, "resolution"))}</p></div></section>" else ""
        val tracker = STAGES.mapIndexed { i, (_, label) ->
            "<li class=\"${if (i < step || s == "completed") "done" else if (i == step) "now" else ""}\"><i>${if (i < step || s == "completed") "<span class=\"material-symbols-rounded\">check</span>" else "${i + 1}"}</i><span>${escape(label)}</span></li>"
        }.joinToString("")
        modal(
            "Ticket $number",
            """
              <header class="cp-hero cp-row-$s">
                <div class="cp-hero-pills"><span class="tk-pill tk-status-$s">${escape(stateLabel(s))}</span><span class="tk-pill tk-priority-${escape(priority)}">${escape(priority.replaceFirstChar { it.uppercase() })} priority</span></div>
                <h3>${escape(title)}</h3>
                <ol class="cp-tracker" aria-label="Progress">$tracker</ol>
              </header>
              <div class="cp-facts">
                <div><span class="material-symbols-rounded" aria-hidden="true">engineering</span><div><label>Assigned to</label><strong>${escape(assignee)}</strong></div></div>
                <div><span class="material-symbols-rounded" aria-hidden="true">event</span><div><label>Raised</label><strong>${escape(formatDate(d.createdAt))}</strong></div></div>
              </div>
              $resolutionHtml
              <section class="cp-block"><label>What you told us</label><p${if (description.isBlank()) " class=\"muted\"" else ""}>${escape(description).ifBlank { "No description provided." }}</p></section>
              ${ticketAttachmentsHtml(d)}
              <h3 class="section-title">Updates</h3>
              <div id="customer-ticket-comments" class="ticket-comments-scroll cp-updates">Loading updates…</div><div id="customer-upload-progress"></div>
              ${if (s == "completed") "<p class=\"cp-closed-note\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">lock</span>${escape(TicketPolicy.COMPLETED_NO_UPDATES)}</p>" else """
              <div class="cp-composer">
                <label for="customer-comment">Add an update</label>
                <textarea class="input" id="customer-comment" rows="3" placeholder="Share more details or ask a question…"></textarea>
                <div class="cp-composer-foot"><label class="cp-attach" for="customer-comment-file"><span class="material-symbols-rounded" aria-hidden="true">attach_file</span> Attach photo or file</label><input id="customer-comment-file" type="file" hidden accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"><span id="customer-comment-file-name" class="field-help"></span></div>
              </div>"""}
            """.trimIndent(),
            // Completed tickets take no more updates (also enforced in firestore.rules).
            if (s == "completed") "" else "<button id=\"customer-send-comment\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">send</span> Send update</button>",
            wide = true,
            extraClass = "cp-modal"
        )
        change("customer-comment-file") {
            element("customer-comment-file-name")?.textContent = selectedFile("customer-comment-file")?.name.orEmpty()
        }
        click("customer-send-comment") { sendComment(record) }
        loadComments(record)
    }

    /** Files added with the ticket (from the create form or the email that opened it). */
    private fun ticketAttachmentsHtml(d: dynamic): String {
        val items = d?.attachments ?: return ""
        val count = items.length as? Int ?: return ""
        val tiles = (0 until count).joinToString("") { i ->
            val item = items[i]
            val url = field(item, "downloadUrl").ifBlank { field(item, "url") }
            val name = field(item, "name").ifBlank { field(item, "fileName").ifBlank { "Attachment ${i + 1}" } }
            val mime = field(item, "mimeType")
            when {
                url.isBlank() -> ""
                field(item, "type") == "image" || mime.startsWith("image/") ->
                    // A thumbnail tile (attachment-tile); tapping it opens the full photo.
                    "<a class=\"attachment-tile image-attachment\" href=\"${escape(url)}\" target=\"_blank\" rel=\"noopener\"><img src=\"${escape(url)}\" alt=\"${escape(name)}\" loading=\"lazy\"></a>"
                field(item, "type") == "video" || mime.startsWith("video/") -> "<div class=\"attachment-tile\"><video src=\"${escape(url)}\" controls></video></div>"
                else -> "<a class=\"btn\" href=\"${escape(url)}\" target=\"_blank\" rel=\"noopener\"><span class=\"material-symbols-rounded\">description</span> ${escape(name)}</a>"
            }
        }
        return if (tiles.isBlank()) "" else "<section class=\"cp-block\"><label>Attachments</label><div class=\"attachment-grid\">$tiles</div></section>"
    }

    private fun loadComments(record: WebRecord) {
        cancelListener(commentsStop)
        val query = CustomerFirebaseGateway.collection("${record.path}/comments")
        commentsStop = CustomerFirebaseGateway.listenQuery(query, { incoming ->
            val comments = incoming.sortedBy { createdMillis(it.data) }
            element("customer-ticket-comments")?.innerHTML = if (comments.isEmpty())
                "<p class=\"empty\" style=\"padding:20px\">No updates yet.</p>"
            else comments.joinToString("") { c ->
                val cd = c.data
                val media = field(cd, "mediaUrl")
                val mediaHtml = if (media.isBlank()) "" else when (field(cd, "type")) {
                    "image" -> "<a class=\"image-attachment\" href=\"${escape(media)}\" target=\"_blank\"><img src=\"${escape(media)}\" alt=\"Update attachment\" loading=\"lazy\"></a>"
                    "video" -> "<video src=\"${escape(media)}\" controls></video>"
                    else -> "<a class=\"btn\" href=\"${escape(media)}\" target=\"_blank\"><span class=\"material-symbols-rounded\">description</span> Open attachment</a>"
                }
                """<div class="card update-card"><div class="update-card-head">${avatarHtml(field(cd, "authorName").ifBlank { "Support team" })}<div class="update-author"><strong>${escape(field(cd, "authorName"))}</strong><small>${escape(formatDate(cd.createdAt))}</small></div></div>${if (field(cd, "text").isBlank()) "" else "<p>${escape(field(cd, "text"))}</p>"}$mediaHtml</div>"""
            }
        }, { message -> element("customer-ticket-comments")?.textContent = message })
    }

    private fun sendComment(record: WebRecord) {
        val text = textarea("customer-comment")
        val file = selectedFile("customer-comment-file")
        if (text.isBlank() && file == null) { toast("Add a message or attachment.", true); return }
        val uid = CustomerFirebaseGateway.currentUser()?.uid as? String ?: return
        val button = element("customer-send-comment") as? HTMLButtonElement
        button?.disabled = true
        fun uploading(percent: Int?) { element("customer-upload-progress")?.innerHTML = if (percent == null) "" else uploadingUpdateHtml(file?.name.orEmpty(), percent) }
        fun post(type: String, mediaUrl: String, storagePath: String) {
            promise(CustomerFirebaseGateway.add("${record.path}/comments", json(
                "ticketId" to record.id, "companyId" to companyId, "authorUid" to uid,
                "authorName" to customerEmail, "text" to text, "type" to type,
                "mediaUrl" to mediaUrl, "storagePath" to storagePath, "createdAt" to CustomerFirebaseGateway.serverTimestamp()
            )), {
                (element("customer-comment") as? org.w3c.dom.HTMLTextAreaElement)?.value = ""
                (document.getElementById("customer-comment-file") as? HTMLInputElement)?.value = ""
                element("customer-comment-file-name")?.textContent = ""
                button?.disabled = false
                uploading(null)
                countActivity("updatesPosted")
                toast("Update sent.")
            }, { message -> button?.disabled = false; uploading(null); toast(message, true) })
        }
        if (file != null) {
            // Photos and videos can take a while on mobile data, so the update shows its upload progress in the list.
            uploading(0)
            uploadCustomerFile(file, { attachment ->
                post(field(attachment, "type"), field(attachment, "url"), field(attachment, "storagePath"))
            }, { message -> button?.disabled = false; uploading(null); toast("Attachment upload failed: $message", true) }) { percent -> uploading(percent) }
        } else {
            post("text", "", "")
        }
    }

    private fun selectedFile(inputId: String): File? =
        (document.getElementById(inputId) as? HTMLInputElement)?.files?.item(0)

    /** Uploads to the customer's own Storage folder (see storage.rules) and returns
     * an attachment record shaped like the admin app's, so ticket/comment rendering
     * needs no customer-specific handling. */
    private fun uploadCustomerFile(file: File, done: (dynamic) -> Unit, failed: (String) -> Unit, onProgress: (Int) -> Unit = {}) {
        val uid = CustomerFirebaseGateway.currentUser()?.uid as? String
        if (uid == null) { failed("Sign in again and retry."); return }
        val safeName = file.name.replace(Regex("[^A-Za-z0-9._-]"), "_")
        val path = "support/customer-uploads/$uid/${js("Date.now()")}-$safeName"
        promise(CustomerFirebaseGateway.uploadWithProgress(path, file, onProgress), { result ->
            val url = field(result, "url")
            val type = if (file.type.startsWith("video/")) "video" else if (file.type.startsWith("image/")) "image" else "file"
            done(json(
                "type" to type, "url" to url, "storagePath" to field(result, "path"),
                "name" to file.name, "mimeType" to file.type
            ))
        }, { message -> failed(message) })
    }

    private fun openNewTicket() {
        modal(
            "New ticket",
            """
              <div class="cp-new-intro"><span class="material-symbols-rounded" aria-hidden="true">support_agent</span><div><strong>Tell us what's wrong</strong><p>Our support team is notified straight away, and you can follow the ticket here until it's fixed.</p></div></div>
              <div class="field"><label for="customer-new-title">What do you need help with?</label><input class="input" id="customer-new-title" maxlength="300" required placeholder="e.g. Printer in reception won't connect"></div>
              <div class="field"><label for="customer-new-description">Details</label><textarea class="input" id="customer-new-description" rows="5" maxlength="3000" placeholder="What happened, where, and when did it start? Any error messages?"></textarea></div>
              <div class="field"><label>Attachment <small>(optional)</small></label><label class="cp-drop" for="customer-new-file"><span class="material-symbols-rounded" aria-hidden="true">add_photo_alternate</span><strong>Add a photo or file</strong><small>A screenshot or photo helps us fix it faster</small></label><input id="customer-new-file" type="file" hidden accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"><div id="customer-new-file-name" class="field-help cp-file-name"></div></div>
            """.trimIndent(),
            "<button id=\"customer-create-ticket\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">send</span> Create ticket</button>",
            extraClass = "cp-modal cp-new"
        )
        change("customer-new-file") {
            element("customer-new-file-name")?.textContent = selectedFile("customer-new-file")?.name.orEmpty()
        }
        click("customer-create-ticket") {
            val title = input("customer-new-title")
            if (title.isBlank()) { toast("Enter a title for your ticket.", true); return@click }
            val description = textarea("customer-new-description")
            val file = selectedFile("customer-new-file")
            val button = element("customer-create-ticket") as? HTMLButtonElement
            button?.disabled = true
            fun create(attachments: List<dynamic>) {
                promise(CustomerFirebaseGateway.callable("createCustomerTicket", json(
                    "title" to title, "description" to description, "attachments" to attachments.toTypedArray()
                )), { result ->
                    val ticketNumber = field(result.data, "ticketNumber")
                    closeModal()
                    countActivity("ticketsCreated")
                    ticketCreatedCelebration(ticketNumber, "Our support team has been notified.")
                }, { message -> button?.disabled = false; toast(message, true) })
            }
            if (file != null) uploadCustomerFile(file, { attachment -> create(listOf(attachment)) }, { message ->
                button?.disabled = false; toast("Attachment upload failed: $message", true)
            }) else create(emptyList())
        }
    }
}
